<template>
  <iq-card class="fuel-card h-100 w-100">
    <div class="fms-card-header">
      <div>
        <h4>Fuel Consumption Today</h4>
        <p class="text-muted">{{ description }}</p>
      </div>
      <span class="fms-tag fms-tag--outline">24H Window</span>
    </div>
    <net-main-engine-consumption-chart
      v-if="hasFlowmeter"
      :series="currAssetSummary.series"
      :title="hasMainEngineFuel ? 'NET MAIN ENGINE CONSUMPTION' : 'FUEL CONSUMPTION (FLOWMETER)'"
      :fuelKeys="flowmeterFuelKeys"
      :netConsumption="hasMainEngineFuel ? meEngineConsValue : totalFuelByFlowmeter"
    />
    <div v-if="flowmeterBlocks.length" class="flow-breakdown">
      <div
        v-for="block in flowmeterBlocks"
        :key="block.key"
        class="flow-block"
        :class="{ 'flow-block--pair': block.items.length === 2 }"
      >
        <div v-for="f in block.items" :key="f.key" class="flow-item">
          <span class="flow-label">{{ f.label }}</span>
          <span
            class="flow-value"
            :class="f.direction === 'out' ? 'flow-value--out' : 'flow-value--in'"
          >{{ f.value }} L</span>
        </div>
      </div>
    </div>
    <div class="fuel-metrics">
      <div class="fuel-metric fuel-metric--total">
        <h5>Total Fuel by Flowmeter</h5>
        <p>{{ totalFuelByFlowmeter }} <span>Liter</span></p>
      </div>
      <div class="fuel-metric" v-if="!isAeConsHidden">
        <h5>Cons. AE Constanta</h5>
        <p>{{ totalAeCons }} <span>Liter</span></p>
      </div>
    </div>
  </iq-card>
</template>

<script>
import NetMainEngineConsumptionChart from '@/components/fms/NetMainEngineConsumptionChart';
import { prettyLabel } from '@/util/ship/vesselDetail';
import isAeConsumptionHidden from '@/util/asset-feature-flags';
import { formatNum } from '@/util/number';

export default {
  name: 'FuelConsumptionCard',
  components: {
    NetMainEngineConsumptionChart,
  },
  props: {
    currAsset: {
      type: Object,
      default: null,
    },
    currAssetSummary: {
      type: Object,
      required: true,
    },
    avgCombinedFlow: {
      type: [String, Number],
      default: '-',
    },
    totalAeCons: {
      type: [String, Number],
      default: '-',
    },
    totalEngineCons: {
      type: [String, Number],
      default: '-',
    },
    isSingleMainEngine: {
      type: Boolean,
      default: false,
    },
    deviceStatus: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    isAeConsHidden() {
      return isAeConsumptionHidden(this.currAsset?.massId);
    },
    hasMainEngineFuel() {
      // Main engine = ada flowmeter posisi (PORT/STARBOARD/CENTER).
      // AE-line (AE4, AE_INLINE, ...) bukan main engine.
      const mePositions = ['PORT', 'STARBOARD', 'CENTER'];
      const groups = this.deviceStatus?.flowmeterGroups || [];
      return groups.some((g) => mePositions.includes(String(g.name).toUpperCase()));
    },
    description() {
      return this.hasMainEngineFuel
        ? 'Main engine fuel consumption trend over the last 24 hours.'
        : 'Auxiliary engine fuel consumption over the last 24 hours.';
    },
    hasFlowmeter() {
      return (this.deviceStatus?.flowmeterGroups || []).length > 0;
    },
    flowmeterFuelKeys() {
      return (this.deviceStatus?.flowmeterGroups || []).map((g) => `${g.name}_FuelCons`);
    },
    totalFuelByFlowmeter() {
      // Total fuel terukur = jumlah konsumsi semua line flowmeter (net utk in-out),
      // mencakup ME (PORT/STBD/CENTER) maupun AE-line (AE4, AE_INLINE, ...).
      const groups = this.deviceStatus?.flowmeterGroups || [];
      const fmSummary = this.currAsset?.summary?.flowmeter?.summary || {};
      let total = 0;
      let any = false;
      groups.forEach((g) => {
        const key = `${g.name.toLowerCase()}EngineCons`;
        const v = Number(fmSummary[key]);
        if (Number.isFinite(v)) {
          total += v;
          any = true;
        }
      });
      return any ? formatNum(total, 2) : '-';
    },
    meEngineConsValue() {
      const raw = this.currAsset?.summary?.flowmeter?.summary?.meEngineCons;
      return Number.isFinite(Number(raw)) ? formatNum(Number(raw), 2) : this.currAssetSummary.fm.meEngineCons;
    },
    flowmeterBlocks() {
      // Breakdown per line flowmeter (label-driven). Satu blok = satu line;
      // line in-out punya 2 item biar bisa dirender bersebelahan.
      const fm = this.currAssetSummary?.fm || {};
      const groups = this.deviceStatus?.flowmeterGroups || [];
      const blocks = [];
      groups.forEach((g) => {
        const data = fm[g.name];
        if (!data) return;
        const items = [];
        if (data.in) {
          items.push({
            key: `${g.name}_IN`,
            label: prettyLabel(`${g.name}_IN`),
            value: data.in.engineCons,
            direction: 'in',
          });
        }
        if (data.out) {
          items.push({
            key: `${g.name}_OUT`,
            label: prettyLabel(`${g.name}_OUT`),
            value: data.out.engineCons,
            direction: 'out',
          });
        }
        if (items.length) blocks.push({ key: g.name, items });
      });
      // Single (1 item) duluan, pair in-out (2 item) belakangan -> pair dapat baris penuh.
      blocks.sort((a, b) => a.items.length - b.items.length);
      return blocks;
    },
  },
};
</script>

<style scoped>
.fuel-card {
  padding: 15px 20px;
}

.flow-breakdown {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin-top: 12px;
}

.flow-block {
  display: flex;
  flex-direction: column;
}

.flow-block--pair {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
}

.flow-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.flow-label {
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #6b7280;
}

.flow-value {
  font-size: 0.9375rem;
  font-weight: 700;
}

.flow-value--in {
  color: #16a34a;
}

.flow-value--out {
  color: #ef4444;
}

.fuel-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.fuel-metric {
  flex: 1 1 140px;
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  padding: 10px 12px;
}

.fuel-metric h5 {
  margin: 0 0 6px;
  font-size: 0.8125rem;
  color: #1d2433;
}

.fuel-metric p {
  margin: 0;
  font-size: 0.875rem;
}

.fuel-metric p span {
  font-size: 0.6875rem;
  color: #6b7280;
}

.fuel-metric-sub {
  display: block;
  margin-top: 2px;
  font-size: 0.6875rem;
  color: #6b7280;
}

.fuel-metric--total {
  background: #eef4ff;
  border-color: #cfe0ff;
}

.fuel-section {
  margin-top: 12px;
  padding: 12px;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
}

.fuel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.fuel-header h5 {
  margin: 0 0 4px;
  font-size: 0.875rem;
}

.fuel-header span {
  font-size: 0.75rem;
  color: #6b7280;
}

.fuel-value {
  text-align: right;
}

.fuel-value strong {
  font-size: 1rem;
  color: #1d2433;
}

.fuel-value span {
  font-size: 0.6875rem;
  color: #6b7280;
}

.fuel-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
}

.fuel-summary div {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  padding: 10px 12px;
}

.fuel-summary h5 {
  margin: 0 0 6px;
  font-size: 0.8125rem;
  color: #1d2433;
}

.fuel-summary p {
  margin: 0 0 4px;
  font-size: 0.875rem;
}

.fuel-total {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf1f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@media screen and (max-width: 991px) {
  .fuel-summary {
    grid-template-columns: 1fr;
  }
}
</style>
